import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Paper, Alert,
} from '@mui/material';
import { ArrowBack, ArrowForward, Check } from '@mui/icons-material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'details', title: 'Session Details' },
  { id: 'schedule', title: 'Schedule & Time' },
  { id: 'review', title: 'Review & Submit' },
];

interface FormValues {
  tutorId: string;
  scheduleId: string;
  studentIds: string[];
  subject: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  notes: string;
}

const INITIAL_VALUES: FormValues = {
  tutorId: '',
  scheduleId: '',
  studentIds: [],
  subject: '',
  sessionDate: '',
  startTime: '',
  endTime: '',
  notes: '',
};

const stepDescriptions: Record<string, string> = {
  details: "Choose which schedule this session belongs to. Sessions are individual occurrences of a schedule.",
  schedule: "Record the actual session date, time, and attendance. Mark each student's attendance status.",
  review: "Review session details before logging. Attendance records will be created for each student.",
};

export const TutoringSessionWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const evaluateRef = useRef<(() => Promise<{ allowed: boolean; reason?: string }>) | null>(null);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    if (evaluateRef.current) {
      const ruleResult = await evaluateRef.current();
      if (!ruleResult.allowed) {
        throw new Error(ruleResult.reason || 'Rule evaluation failed');
      }
    }

    const entityResult = await dataProvider.create('tutoring-sessions', {
      data: {
        tutorId: v.tutorId,
        scheduleId: v.scheduleId,
        studentIds: v.studentIds,
        subject: v.subject,
        sessionDate: v.sessionDate,
        startTime: v.startTime,
        endTime: v.endTime,
        notes: v.notes,
        status: 'Draft',
      },
    });

    const entityId = entityResult.data.id;
    const { finalState, transitions } = buildTransitionChain('tutoring-session', 'draft', ['SCHEDULE']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'tutoring-session',
        entityId,
        entityName: `${v.subject} - ${v.sessionDate}`,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, validateStep: validate, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate } = useRuleEvaluation({
    ruleId: 'tutoring-session-guards',
    context: values as unknown as Record<string, unknown>,
  });
  useEffect(() => {
    evaluateRef.current = evaluate;
  }, [evaluate]);

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const vals = values as unknown as FormValues;

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'details':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Tutor ID" value={vals.tutorId} onChange={e => updateField('tutorId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Schedule ID" value={vals.scheduleId} onChange={e => updateField('scheduleId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Subject" value={vals.subject} onChange={e => updateField('subject', e.target.value)} fullWidth />
            </Grid>
          </Grid>
        );
      case 'schedule':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Session Date" type="date" value={vals.sessionDate} onChange={e => updateField('sessionDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Start Time" type="time" value={vals.startTime} onChange={e => updateField('startTime', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="End Time" type="time" value={vals.endTime} onChange={e => updateField('endTime', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Notes" value={vals.notes} onChange={e => updateField('notes', e.target.value)} fullWidth multiline rows={3} />
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review session details before creating. A workflow will be initialized in Draft state.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Session Info</Typography>
                <Typography variant="body2"><strong>Tutor:</strong> {vals.tutorId}</Typography>
                <Typography variant="body2"><strong>Subject:</strong> {vals.subject}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {vals.sessionDate}</Typography>
                <Typography variant="body2"><strong>Time:</strong> {vals.startTime} - {vals.endTime}</Typography>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Tutoring Session Created"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Tutor', value: vals.tutorId },
            { label: 'Subject', value: vals.subject },
            { label: 'Date', value: vals.sessionDate },
          ]}
          footerActions={[
            { label: 'View Sessions', to: '/tutoring-sessions' },
            { label: 'Create Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
